import { TicketVoucherType } from '@nf/types/ticket';

export const SPORT_TYPE = {
	ESports: 43,
	MuayThai: 44,
	NumberGame: 161,
	Happy5: 164,
	BitCoin: 175,
	Outright: 999,
} as const;

export const BET_TYPE = {
	Hdp: '1',
	TimeMachineOu: '228',
	TimeMachineHdp: '229',
	PenaltyShootoutCombo: '376',
	ScoreBoxHdp: '468',
	ScoreBoxOu: '469',
	DoubleQuarterWinner: '642',
	HdpOuResult: '646', // same as 'Match Handicap & Total'
	BackLay_1X2: '9538',
	BackLay_MatchWinner: '9539',
	BackLay_SuperOverWinner: '9540',
	BaseBall_8825: '8825',
} as const;

export const BETTING_CODE = {
	MinBetGreaterThanMaxBet: -93,
	RejectTicket: -94,
	ExceedLimitSelection: -95,
	SameMatch: -96,
	NotSupportParlay: -97,
	OddsChangeForOnlyParlay: -98,
	ExceedLimitSelectionBBPlus: -100,
	BetFailed: -1,
	Waiting: 0,
	Accepted: 1,
	Suspended: 2,
	OverMaxStakePerMatch: 7,
	CreditProblem: 8,
	DisableBetProblem: 10, // for single wallet
	OddsChange: 15,
	LineChanged: 45,
	ParlayMaxBetLessThanMinBet: 52,
	ParlayNoComboAvailable: 53,
	OverMaxPayoutPerMatch: 56,
	ParlayMinOdds: 60,
	ParlayComboByPool: 61,
	BlockDoubleBet: 65,
	ClosePrice: 46,
	BBPlusRequireSmp: 70,
	EventClosed: 6,
	DisabledParlay: 58,
} as const;

export const isAcceptedBetTicket = (ticket: { Code: number }) =>
	([BETTING_CODE.Waiting, BETTING_CODE.Accepted] as Array<number>).includes(ticket.Code);

export const HIDDEN_BETTING_CODES: Array<number> = [37, 43, 90];

export const CricketPlayerBetTypeList = [
	9662, 9663, 9664, 9665, 9666, 9667, 9676, 9677, 9678, 9679, 9680, 9681, 9682, 9683, 9684, 9685, 9686, 9687, 9688, 9689,
	9690, 9691, 9692, 9693, 9694, 9695, 9712, 9713, 9714, 9715, 9716, 9717, 9718, 9719, 9720, 9721, 9730, 9731, 9732, 9733,
	9734, 9735, 9736, 9737, 9738, 9739, 9696, 9697, 9698, 9699, 9702, 9703, 9704, 9705
]

export const BackLayPayoutSetting = {
	betType: [BET_TYPE.BackLay_1X2, BET_TYPE.BackLay_MatchWinner, BET_TYPE.BackLay_SuperOverWinner] as string[],
	profitBetTeam: ['1b', '2b', 'xb', 'hb', 'ab'],
	liabiltyBetTeam: ['1l', '2l', 'xl', 'hl', 'al']
}

export interface CanBetResultModel {
	innerMsg: BetMsgModel[];
	outerMsg: BetMsgModel[];
	canBet: boolean;
	confirmBetMsg: BetMsgModel[];
}

export interface BetMsgModel {
	ticketKey: string;
	code: number;
	msg: string;
	msgResourceKey?: string;
	canBet: boolean;
	cssStatus?: 'closed' | 'same-match' | 'warning' | undefined;
}

export interface BettingCodeMap {
	title: string;
	canBet: boolean;
	msg?: string;
	msgResourceKey?: string;
	cssStatus?: 'closed' | 'same-match' | 'warning';
	showToast?: boolean;
}

/**
 * BETTING CODE 定義
 * @description 前端自訂請以 "負數" 定義, 其餘皆為 betting api 實際會回傳的 error code
 * @remark ※注意: canBet 狀態已對照舊 code, 不可隨意更動
 */
export const BETTING_CODE_MAP = new Map<number, BettingCodeMap>([
	[-93, { title: 'MinBetGreaterThanMaxBet', canBet: true, msgResourceKey: 'lbl_limitbet' }],
	[-94, { title: 'RejectTicket', canBet: false }],	// only show at ConfirmBet
	[-95, { title: 'ExceedLimitSelection', canBet: true, msgResourceKey: 'TicketOver' }],	// 上限20張
	[-96, { title: 'SameMatch', canBet: true, msgResourceKey: 'lbl_ESOSrelate', cssStatus: 'same-match' }],	// 相同賽事
	[-97, { title: 'NotSupportParlay', canBet: true, msgResourceKey: 'lbl_EDSupport' }],	// 不支援串關
	[-98, { title: 'OddsChangeForOnlyParlay', canBet: true, msgResourceKey: 'lbl_oddchange' }],
	[-99, { title: 'BetbyTicketError', canBet: false }], // betby ticket error
	[-100, { title: 'ExceedLimitSelectionBBPlus', canBet: true, msgResourceKey: 'TicketOver' }],	// BBPlus上限15個group
	[-1, { title: 'BetFailed', canBet: true }],
	[0, { title: 'Waiting', canBet: true }],
	[1, { title: 'Accepted', canBet: true }],
	[2, { title: 'Suspended', canBet: true }],	// only show at ConfirmBet
	[6, { title: 'MatchClosed', canBet: false }],
	[7, { title: 'OverMaxPerMatch', canBet: true, cssStatus: 'closed', showToast: true }],
	[8, { title: 'CreditProblem', canBet: true, cssStatus: 'closed', showToast: true }], // insufficient balance
	[10, { title: 'DisableBetProblem', canBet: true }], // for single wallet (include single wallet api error code 201~205,502,506,507)
	[15, { title: 'OddsChange', canBet: true }],
	// [37, { title: 'DisabledBet', canBet: false }],
	[41, { title: 'InPlayChanged', canBet: true }], // for e-sports
	// [43, { title: 'OverMaxPerBet', canBet: true }],
	[44, { title: 'ScoreChanged', canBet: true }], // api return code=15
	[45, { title: 'LineChanged', canBet: true }], // api return code=15
	[46, { title: 'ClosePrice', canBet: false }], // api return code=6
	[47, { title: 'MarketClosed', canBet: false }], // api return code=6
	[48, { title: 'OverRepeatedBet', canBet: false }],
	[49, { title: 'BetIntervalBlock', canBet: true }],
	[52, { title: 'ParlayMaxBetLessThanMinBet', canBet: false, cssStatus: 'closed' }], // parlay max payout
	[53, { title: 'ParlayNoComboAvailable', canBet: false, cssStatus: 'closed' }], // parlay max payout
	[55, { title: 'DummyErrorCodeUseByBettingModule', canBet: false }], // disable outright
	[56, { title: 'OverMaxPayoutPerMatch', canBet: true, cssStatus: 'closed', showToast: true }],
	[58, { title: 'DisableParlay', canBet: true, cssStatus: 'closed' }], // CC限制
	[60, { title: 'ParlayMinOdds', canBet: false, cssStatus: 'warning' }],
	[61, { title: 'ParlayComboByPool', canBet: false, cssStatus: 'warning' }],
	[65, { title: 'BlockDoubleBet', canBet: true }],
	[70, { title: 'BBPlusRequireSmp', canBet: true, cssStatus: 'warning' }], // 組成的BB+ 必須有一張注單是同場串關
	// [90, { title: 'TransIdError', canBet: true }],
]);

export type ChildTabType = 'WinningRate' | 'Turnover' | 'Payout' | 'HotMatch' | 'HotBetting' | 'null';

export type BetSourceType = 'promotion' | undefined;

/** 合併投注, 分開投注被選取時使用 */
export type NormalBetslipTab = 'single' | 'parlay';

// 使用者選擇的投注單tab
export type UserBetSlipTab = 'single' | 'parlay' | 'bb-plus' | 'standard';

/** 基本樣式, 懸浮樣式使用 */
export enum BetslipStyle {
	basic = 0,
	floating = 1
}

/** Betslip - UI 元件類型 */
export type BetslipType = 'single' | 'multi' | 'quickbet' | 'empty';

/** stake per bet 放置類型 */
export type StakePerBetType = 'multi' | 'parlay';

/** 下注後是否收票可能的狀態 */
export type AcceptTicketStatus = 'running' | 'waiting' | 'waitting' | 'reject';

/**可選擇的優惠券類型 for Saba Reward Program */
export type VoucherType = 
	| 'OddsBooster-promo' 
	| 'OddsBooster' 
	| 'RiskFreeBet' 
	| 'NewRiskFreeBet' 
	| 'GiftRiskFreeBet'
	| 'AffPromotionOddsBoost'
	| 'FreeBet'
	| 'None';

export const BetAction = {
	Default: 0,
	QuickBet: 1,
	TelegramBet: 2,
	BetAgain: 4,
	ReservationBet: 8,
	BetGenerator: 16,
	TournamentWidget: 32,
	MasterRank: 64,
	SBRWidget: 1024,
	BetBuilderPlus: 2048,
	// TODO: 有看到公版有2052 待確認與實作
};

export const bettingInputMaxLength = 8;
export const selectTicketMaxLength = 20;
export const selectTicketGroupMaxLengthBBPlus = 15;

export interface AddTicketEventInterface {
	ticketModel: OddsTicketModel;
	oddsKey: string;
}

export interface RemoveTicketEventInterface {
	oddsKey: string;
}

/** Betslip - for odds button add ticket */
export interface OddsTicketModel {
	Ascore: number;
	ChoiceValue?: number;
	Hdp1: number;
	Hdp2: number;
	Hscore: number;
	LeagueId: number;
	LeagueGroupId: string;
	Line: string;
	NumberLine?: number; // for BB+ mode, the line of the ticket disable
	MRPercentage: unknown;
	Matchid: number;
	OtherHdp: number | undefined;
	Parentid: number;
	Parentmatchid: number;
	Resourceid: string;
	SrcOddsInfo?: string | null;
	away: string;
	betteam: string;
	bettype: number;
	errorcode?: string;
	gameid: number;
	isMatchHasCashOut: boolean;
	home: string;
	isMMR: boolean;
	odds: string;
	oddsid: number;
	pty: number;
	srcLine: number;
	stake?: number;
	Cashout?: number;
	type: 'OT' | 'OU';
	originalPrice: number;
	HomeId: number;
	AwayId: number;
	isSBR?: boolean;
	IsTest?: boolean;
	SubType?: number;
}

export type BettingCommon = {
	ErrorCode: number;
	ErrorMsg: string;
};

/** Betslip - 單一 ticket (from Multi) */
export type TicketModel = {
	key: string;
	stake: number;
	sync: boolean; // for determine this ticket has been sync from db
	payout: number;
	minBet: number;
	maxBet: number;
	Minbet: string;
	Maxbet: string;
	oldOdds: string; // for test
	newOdds: string; // for test
	hasCashOut: boolean; // for test

	// === api response ===
	AutoAcceptSec: string;
	AwayId: number;
	AwayName: string; // default=""
	Bet: string;
	BetHintMsg: string; // default=""
	BetID: string; // default=null
	BetRecommends: any;
	BetTimeConstraint: {
		EndTimestamp: number;
	};
	Betteam: string; // default=""
	Bettype: string; // default="0"
	BettypeName: string; // default=""
	BonusID: number;
	BonusType: number;
	CategoryName?: string; // for kero player props
	ChoiceValue: string; // default="" (some html string)
	Code: number; // default=-99
	Common?: BettingCommon;
	DisplayHDP: string; // default="0"
	PreDisplayOdds: string;
	DisplayOdds: string; // default="0"
	// 以下兩欄位是用於parlay tab時要顯示parlayOdds, 可是目前架構只會用到single ticket因此將parlay odds開到此欄位上
	PreDisplayParlayOdds: string;
	DisplayParlayOdds: string; // default="0"
	DisplayOddsPair: string; // default="0"
	DisplayTime: string;
	ErrorCode: number;
	GameName: string; // default=""
	Guid: string;
	HasParlay: boolean; // default=false
	Hdp1: number; // default=0
	Hdp2: number; // default=0
	HomeId: number;
	HomeName: string; // default=""
	IsInPlay: boolean; // default=false
	IsLive: boolean; // default=false
	CheckWaitingTicket: boolean;
	BetDelaySec: number;
	LeagueId: number;
	LeagueGroupId: number;
	LeagueName: string; // default=""
	Line: string;
	NumberLine?: number; // for BB+ mode, the line of the ticket disable status
	LiveAwayScore: number; // default=999
	LiveHomeScore: number; // default=999
	LiveScore: boolean; // default=false
	MRPercentage: string; // default=""
	MatchCode: any;
	Matchid: number; // default=-1
	Message: string | null;
	Nolan: boolean;
	OddsBeforeOddsBoost: string;
	OddsBoost: any;
	OddsID: number; // default=0
	OddsInfo: string | null; // default=""
	OddsStatus: any;
	originalPrice: number;
	PDHyalpsiD: string;
	ParentMatchid: number;
	/** BB+ compareBetBuilderOdds / quarter markets — carried from OddsTicketModel */
	Resourceid?: string;
	OddsType?: number;
	PriceType: number;
	QuickBet: string;
	RecommendType: number;
	SeqNo: number;
	SportName: string; // default=""
	SportType: number; // default=0
	SrcOddsInfo?: any;
	SuggestStake: number;
	Tenet: boolean;
	TicketTime: number;
	TicketType: string; // default="-"
	UseBonus: number;
	isLineChange: boolean;
	isMMR: boolean; // default=false
	isOddsChange: boolean; // default=false
	isScoreChange: boolean;
	isSBR?: boolean;
	riaPsddOyalpsiD: string;
	sddOyalpsiD: any;
	sinfo: string; // default=""
	TransId?: string;
	ACCode?: string;
	specialChoices?: Array<string>;
	CustomCurrencyName?: string;
	IsCashoutEnabled?: boolean;
	RewardModel?: TicketRewardModel | undefined | null;
	OddsBoostType?: number;
	IsTest?: boolean;
	SubType?: number;
	// === betting related model ===
	// programID: string; // default=""
	// raceNum: string; // default=""
	// runner: string; // default=""
	// IsCashOutTicket: boolean; // default=false
	// IsHomeFavor: boolean; // default=false
	// IsAwayFavor: boolean; // default=false
	// HasSameMatch: boolean; // default=false
	// acceptBetterOdds: boolean; // default=null
	// bonusType: string; // default=""
	// bonusId: string; // default=""
	// canRetain: boolean; // default=false
	// category: number; // default=-1
};

export type BetTicketModel = {
	Key: string;
	DisplayOdds: string;
	OddsBeforeOddsBoost: any | null; // Placeholder, needs proper type
	OddsBoost: any | null; // Placeholder, needs proper type
	MRPercentage: number;
	DisplayHDP: string;
	Hdp1: number;
	Hdp2: number;
	OddsInfo: any | null; // Placeholder, needs proper type
	sinfo: string;
	SrcOddsInfo: any | null; // Placeholder, needs proper type
	BetID: string;
	LeagueGroupId: number;
	BetTeam: any; // Placeholder, needs proper type
	ChoiceValue: any; // Placeholder, needs proper type
	TransId_Cash: string;
	Code: number;
	Message: string;
	isOddsChange: boolean;
	isLineChange: boolean;
	isScoreChange: boolean;
	Stake: string;
	Stake_Cash: string;
	Stake_Bonus: string;
	ActualStake_Cash: string;
	ActualStake_Bonus: string;
	LiveHomeScore: number;
	LiveAwayScore: number;
	TicketStatus: number;
	IsInPlay: boolean;
	IsLive: boolean;
	HasParlay: boolean;
	PriceType: number;
	TotalPerBet: number;
	FinalBalance: string;
	AdjustedMaxBet: number;
	CheckWaitingTicket: boolean;
	BetDelaySec: number;
	ErrorCode: number;
	TicketJson: string;
	Common: BettingCommon;
	ChkSetting: any; // Placeholder, needs proper type
	ExtInfo: any; // Placeholder, needs proper type
	PT: any; // Placeholder, needs proper type
	Commission: any; // Placeholder, needs proper type
	Reserved_1: number;
	Reserved_2: number;
	BetTimeConstraint: any; // Placeholder, needs proper type
	BetActionTypeEnum: number;
	SportTypeEnum: number;
	SiteAutobookieGroup: number;
	OddsGroupGap: number;
	SingleWayAutobookieGap: number;
	UserActualrate: number;
	ErrorCodeEnum: number;
	FinalBetDelaySec: number;
	SingleWalletFinalBalance: number;
	TransactionTime: string;
	CreditSiteSpread: number;
	IsFavorTeam: boolean;
	GlobalShowTime: string;
	DisplayTime: string;
	CurrencySpread: number;
	MarketEnum: number;
	BetRecommendation: any[]; // Placeholder, needs proper type
	BetRecommends: any[]; // Placeholder, needs proper type
	ACCode: any; // Placeholder, needs proper type
	UnderOverTicket?: UnderOverTicketModel;
	CategoryName?: string;
}

export type UnderOverTicketModel = {
	RewardModel: TicketRewardModel | null;
	MatchID: number;
	OddsBoostType: number;
	TransID: number;
};

export type TicketRewardModel = {
	UseVoucher: TicketVoucherType;
	AmountOffBoostOdds: number;
	QuotaOffRiskFreeVoucher: number;
	VoucherId: number;
	AgentPositionTaking: number;
};

export type MultiTicketsModel = {
	Common: BettingCommon;
	ErrorMsg: string;
	ItemList: Array<BetTicketModel>;
};

/** Betslip - 串關 ticket (from Parlay) */
export type ParlayTicketModel = {
	key: string;
	stake: number;
	payout: number;

	// === api response ===
	BetCheck: number;
	BetCount: number;
	BetTeam: number;
	CanBet: boolean;
	DoPlaceBet: boolean;
	ErrorCode: number;
	ErrorMessageDetails: Map<string, string>;
	ErrorMsg: string;
	MaxBet: number;
	MaxPayout: number;
	Name: string;
	Odds: number;
	Type: number;
	isComboParlay: boolean;
	isLucky: boolean;
	isMixParlay: boolean;
	isSystemParlay: boolean;
	Stake: number;
	TotalStake: number;
	CustomCurrencyName?: string;
}

/** Betslip - Leg Odds DTO (from Parlay) */
export type LegOddsDto = {
	ErrorCode: number;
	ErrorMessage: string;
	MatchId: number;
	MatchIdList?: number[];
	TransIds?: number[];
	HomeName?: string;
	AwayName?: string;
	LeagueName?: string;
	PreOdds?: number; // 後來加上的欄位, 用於 BB+ 模式下, 顯示 Odds 變化前後的數值
	shouldShowOddsChange?: boolean; // 後來加上的欄位, 用於 BB+ 模式下, 是否顯示 Odds 變化
	Odds: number;
}

/** Betslip - 多筆串關的資料集 (from Parlay) */
export type ParlayTicketsModel = {
	CanBetTicketCnt: number;
	MaxBet: string;
	MinBet: string;
	Common: BettingCommon;
	Code: number;
	Message: string;
	Tickets: Array<TicketModel>,
	comboData: Array<ParlayTicketModel>;
	LegOdds: Array<LegOddsDto>;
	CheckWaitingTicket: boolean;
	TransId_Combo: string;
	TransId_Lucky: string;
	TransId_System: string;
};

/** Betslip - 多筆串關的資料集 (from Parlay) */
export type BetParlayTicketsModel = {
	CanBetTicketCnt: number;
	MaxBet: string;
	MinBet: string;
	Common: BettingCommon;
	Code: number;
	Message: string;
	FinalBalance: string;
	ItemList: Array<BetTicketModel>,
	comboData: Array<ParlayTicketModel>;
	CheckWaitingTicket: boolean;
	LegOdds: Array<LegOddsDto>;
	TransId_Combo: string;
	TransId_Lucky: string;
	TransId_System: string;
};

/** Betslip - API GetComboTickets Response */
export type SyncTicketsResponse = {
	MultiData: Array<TicketModel>,
	ParlayData: ParlayTicketsModel,
	OddsPromotionCode?: string
}

/** Betslip - API DoComboTicketsBet Response */
export type BetTicketsResponse = {
	MultiData: MultiTicketsModel,
	ParlayData: BetParlayTicketsModel
}

export type ParlayTicketStatus = {
	Master: TicketStatus,
	Slave: Array<ParlaySubTicket>
}

export type TicketStatus = {
	status: string,
	ReasonID?: number,
	refno: number
}

export type ParlaySubTicket = {
	MatchId: number
	Status: string,
	ReasonIdm?: number,
	ParentMatchId: number
}